// Brand expertise — canonical-count-based brand weighting.
//
// THE PROBLEM: a single product can carry multiple brand names. Both
// "Slim Jim" and "Buffalo Wild Wings" appear on a "Slim Jim Buffalo
// Wild Wings Buffalo Cheddar Cheese Beef Stick" — but Slim Jim is
// the manufacturer (they make beef sticks); BWW is the licensing
// collaborator (they make chicken wings). A naive
// "first-brand-detected wins" loses this.
//
// THE MODEL: each brand has an EXPERTISE histogram — a count of
// canonicals (and hubs / categories) we've seen them produce.
//   * Slim Jim — heavily weighted on `beef_stick` / `beef_jerky` /
//     `snack_stick` (their entire SKU line). On a beef-stick scan,
//     Slim Jim's canonical-match score dominates.
//   * Buffalo Wild Wings — heavily weighted on `chicken_wings` /
//     `hot_sauce` / `ranch_dressing` (their actual product line).
//     On a beef-stick scan, BWW's score is ~zero.
//   * Boar's Head — heavily weighted on deli meats: `turkey`, `ham`,
//     `salami`, `pastrami`, `roast_beef`, `prosciutto`. On a turkey
//     scan with Mike's Hot Honey co-branding, Boar's Head's
//     canonical-match for `turkey` overpowers Mike's empty meat
//     score.
//   * Mike's Hot Honey — weighted only on `honey` /
//     pantry-sweeteners. Their meat-canonical score is zero.
//
// THE SCORING LAYERS (strongest → weakest):
//   1. canonicals[canonicalId] — exact canonical match. The brand
//      directly produces this SKU. Multiplier ×100.
//   2. hubs[hubId]              — same-hub match. The brand makes
//      something else in this hub (Boar's Head does turkey AND ham,
//      both pork_hub-adjacent / meat). Multiplier ×10.
//   3. categories[category]     — same-registry-category match. The
//      brand makes something else in "meat" or "dairy" or "pantry".
//      Multiplier ×1.
// Sum all three for the brand's score against this product. Highest
// score = primary brand. Ties break by total observation count
// (the brand we know the most about overall).
//
// PHASE 1 — TODAY: this file is a curated seed. The counts are
// estimates of each brand's SKU breadth in each canonical/hub/
// category, NOT real measurements. Seeded for the top ~30 brands
// where multi-brand collisions are common.
//
// PHASE 2 — DB-TIER LEARNING (planned): a future migration adds
// `brand_canonical_observations(brand_slug, canonical_id, count,
// last_seen_at)` populated from every (brand, canonical_id) write
// in `barcode_identity_corrections` and `pantry_items`. The picker
// merges seed counts + DB counts; over time the seed becomes a
// fallback for cold-start while measured data takes over.
//
// MAINTENANCE PRINCIPLE: only seed brands that:
//   1. Have a recognizable product LINE (not a one-SKU brand).
//   2. Are likely to co-brand (collab / licensing / private label).
// One-SKU brands don't need expertise weighting; the single brand
// is always primary.

// Normalize a brand display name to a stable lookup slug. Lowercase,
// alphanumeric, underscore-separated. Three pre-passes before the
// alphanumeric collapse:
//
//   1. Diacritic strip (NFD + combining-mark removal) — "Häagen-Dazs"
//      → "haagen_dazs" instead of "h_agen_dazs".
//   2. Apostrophe strip — "Mike's Hot Honey" → "mikes_hot_honey"
//      instead of "mike_s_hot_honey".
//   3. Ampersand strip — "M&M's" → "mms", "Ben & Jerry's" →
//      "ben_jerrys". Treating "&" as a word separator would leave
//      "m_ms" / "ben_jerrys" — only the latter is desirable.
//
// Without these pre-passes the slugs miss the expertise lookup.
// Strict-mode triggers. When a canonical resolves to one of these
// subtypes OR categories, the Tier 5 gate runs in strict mode: no
// claim from any source = the brand doesn't apply. Reason: most
// brands don't make fresh produce; without explicit claim, default
// to filter rather than letting Haiku word-association
// hallucinations (Coca-Cola for "Anaheim Pepper" because it
// contains the word "pepper") pass through.
//
// CATEGORY-level triggers catch the "subtype is set to something
// specific the seed function chose (chile, pepper, leafy, root,
// berry, citrus, fungi, etc.) that isn't enumerated below" case.
// Anaheim's subtype is "chile" — not in NARROW_SUBTYPES — but its
// category is "produce" → strict mode still fires correctly.
//
// For broader subtypes (cheese, pasta, sauce) and broader
// categories (dairy, pantry, beverage) the gate stays permissive:
// most brands genuinely cover those, and no-claim is "we don't
// have data yet" rather than "doesn't apply."
const NARROW_SUBTYPES = new Set([
  "vegetable_fresh",
  "fruit_fresh",
  "herb_fresh",
]);
const NARROW_CATEGORIES = new Set([
  "produce",
]);

// Specialty subcategories — when a canonical lives in one of these,
// the picker's subtype-claim tiers (1.5 curated, 1.7 identity, 2.5
// derived) DO NOT fire. Reason: subtype-claim is "this brand makes
// cheese" — fine for commodity cheeses (cheddar, mozzarella,
// parmesan, gouda) where almost every grocery private label has
// a version. NOT fine for specialty cheeses (gjetost, manchego,
// roquefort, scamorza) where most private labels DON'T carry a
// version. For specialty canonicals, only per-canonical evidence
// (Tier 1 curated.canonicals[id], Tier 2 derived.canonicals[id],
// Tier 3 KNOWN_BRANDS hint, Tier 4 observations, Tier 5
// info.brands.common) surfaces brands. Empty picker is preferred
// over "Great Value gjetost" — the user wants strict but accurate.
const SPECIALTY_SUBCATEGORIES = new Set([
  "Scandinavian / Specialty",
  "Smoked",
  "Blue",
  "Alpine",
  "Washed Rind",
  "American Originals",
  "Hard / Aged",      // parm/pecorino/aged-gouda — niche, mostly imported or specialty
  "Fresh / Unaged",   // burrata, mascarpone — mostly specialty
  "Soft Ripened",     // brie/camembert — specialty
]);

// Subtype tokens may arrive from multiple sources (curated JS files,
// Haiku output, USDA-derived) with inconsistent casing/separators.
// Normalize to lowercase snake_case for set comparison.
//   "Ice Cream" / "ice-cream" / "ice_cream" → "ice_cream"
function normSubtype(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[\s\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}
function subtypeSetFrom(arr) {
  if (!Array.isArray(arr)) return null;
  const out = new Set();
  for (const s of arr) {
    const n = normSubtype(s);
    if (n) out.add(n);
  }
  return out.size > 0 ? out : null;
}

export function brandSlugify(display) {
  return String(display || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")  // strip combining diacritics
    .replace(/[‘’'`&]/g, "")                  // strip apostrophes + ampersands
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Curated expertise table. brand_slug → { canonicals, hubs,
// categories, primaryHubsOrdered }. The numbers are estimated
// SKU-line counts: "Slim Jim has ~30 beef_stick variants in their
// product catalog" → canonicals.beef_stick = 30. They are guesses
// today; Phase 2 replaces them with measured counts.
//
// `primaryHubsOrdered` is a quick-lookup ordering of the brand's
// strongest hubs from most-known to least-known. Useful for
// surfacing "what is this brand best known for" queries without
// re-sorting the histogram.
export const BRAND_EXPERTISE = {
  // ── Snack meats / jerky ─────────────────────────────────────────
  slim_jim: {
    canonicals: {
      beef_stick:    30,
      beef_jerky:    18,
      snack_stick:   12,
      pork_stick:     4,
    },
    hubs:       { beef_hub: 50, pork_hub: 4 },
    categories: { meat: 64 },
    subtypes:   ["snack_meat"],
    primaryHubsOrdered: ["beef_hub"],
  },
  jack_links: {
    canonicals: {
      beef_jerky:    25,
      beef_stick:     8,
      turkey_jerky:   6,
      pork_jerky:     3,
    },
    hubs:       { beef_hub: 33, turkey_hub: 6, pork_hub: 3 },
    categories: { meat: 42 },
    subtypes:   ["snack_meat"],
    primaryHubsOrdered: ["beef_hub", "turkey_hub", "pork_hub"],
  },

  // ── Wings / chicken-forward ─────────────────────────────────────
  buffalo_wild_wings: {
    // BWW's actual product line is wings + sauces + dressings; the
    // beef-stick collab is a licensing exception encoded in subtypes
    // so the picker treats them as a credible co-brand on snack
    // meats even though they don't manufacture there.
    canonicals: {
      chicken_wings:        24,
      hot_sauce:             8,
      ranch_dressing:        4,
      blue_cheese_dressing:  3,
      bbq_sauce:             3,
    },
    hubs:       { chicken_hub: 24 },
    categories: { meat: 24, pantry: 18 },
    subtypes:   ["wing", "sauce", "dressing", "snack_meat"],
    primaryHubsOrdered: ["chicken_hub"],
  },
  perdue: {
    canonicals: {
      chicken:           18,
      chicken_breast:    14,
      chicken_thighs:    10,
      ground_chicken:     6,
      chicken_wings:      8,
      turkey:             4,
    },
    hubs:       { chicken_hub: 56, turkey_hub: 4 },
    categories: { meat: 60 },
    subtypes:   ["chicken_cut", "wing", "deli"],
    primaryHubsOrdered: ["chicken_hub", "turkey_hub"],
  },
  tyson: {
    canonicals: {
      chicken:           24,
      chicken_breast:    18,
      chicken_thighs:    14,
      ground_chicken:    10,
      chicken_nuggets:    8,
      chicken_wings:     12,
      bacon:              4,
    },
    hubs:       { chicken_hub: 86, pork_hub: 4 },
    categories: { meat: 90 },
    subtypes:   ["chicken_cut", "wing", "nugget", "sausage"],
    primaryHubsOrdered: ["chicken_hub"],
  },

  // ── Deli meats ──────────────────────────────────────────────────
  boars_head: {
    canonicals: {
      turkey:           14,
      ham:              12,
      salami:            8,
      prosciutto:        4,
      pastrami:          6,
      roast_beef:        5,
      mortadella:        3,
      bologna:           3,
      capicola:          3,
      cheddar:           8,
      swiss:             6,
      american_cheese:   5,
      provolone:         5,
      muenster:          3,
    },
    hubs:       { turkey_hub: 14, beef_hub: 11, pork_hub: 30, cheese_hub: 27 },
    categories: { meat: 55, dairy: 27 },
    subtypes:   ["deli", "cheese"],
    primaryHubsOrdered: ["pork_hub", "cheese_hub", "turkey_hub", "beef_hub"],
  },
  applegate: {
    canonicals: {
      turkey:        8,
      ham:           5,
      bacon:         6,
      hot_dogs:      8,
      bologna:       3,
      salami:        4,
      sausage:       6,
      chicken:       5,
    },
    hubs:       { turkey_hub: 8, pork_hub: 28, chicken_hub: 5 },
    categories: { meat: 45 },
    subtypes:   ["deli", "sausage"],
    primaryHubsOrdered: ["pork_hub", "turkey_hub"],
  },
  oscar_mayer: {
    canonicals: {
      hot_dogs:        12,
      bacon:            8,
      bologna:          6,
      ham:              6,
      turkey:           6,
      salami:           3,
      lunchables:       8,
    },
    hubs:       { pork_hub: 35, turkey_hub: 6 },
    categories: { meat: 49 },
    subtypes:   ["deli", "sausage", "boxed_meal"],
    primaryHubsOrdered: ["pork_hub", "turkey_hub"],
  },
  hillshire_farm: {
    canonicals: {
      sausage:         15,
      kielbasa:         5,
      smoked_sausage:   6,
      turkey:           4,
      ham:              4,
    },
    hubs:       { pork_hub: 30, turkey_hub: 4 },
    categories: { meat: 34 },
    subtypes:   ["sausage", "deli"],
    primaryHubsOrdered: ["pork_hub"],
  },

  // ── Sauces / condiments / sweeteners ────────────────────────────
  mikes_hot_honey: {
    canonicals: {
      honey:           8,
      hot_honey:       4,
    },
    hubs:       {},  // no honey hub in the registry
    categories: { pantry: 12 },
    subtypes:   ["sauce", "sweetener"],
    primaryHubsOrdered: [],
  },
  franks_redhot: {
    canonicals: {
      hot_sauce:       12,
      buffalo_sauce:    4,
      wing_sauce:       4,
    },
    hubs:       {},
    categories: { pantry: 20 },
    subtypes:   ["sauce"],
    primaryHubsOrdered: [],
  },
  cholula: {
    canonicals: {
      hot_sauce:       10,
    },
    hubs:       {},
    categories: { pantry: 10 },
    subtypes:   ["sauce"],
    primaryHubsOrdered: [],
  },
  heinz: {
    canonicals: {
      ketchup:         12,
      mayonnaise:       8,
      bbq_sauce:        6,
      yellow_mustard:   4,
      relish:           4,
      worcestershire:   3,
    },
    hubs:       {},
    categories: { pantry: 37 },
    subtypes:   ["sauce", "condiment"],
    primaryHubsOrdered: [],
  },
  hellmanns: {
    canonicals: {
      mayonnaise:      14,
      ranch_dressing:   3,
    },
    hubs:       {},
    categories: { pantry: 17 },
    subtypes:   ["condiment", "dressing"],
    primaryHubsOrdered: [],
  },
  kraft: {
    canonicals: {
      mac_and_cheese:    12,
      mayonnaise:         8,
      bbq_sauce:          6,
      cheese_singles:    10,
      shredded_cheese:   12,
      ranch_dressing:     5,
      italian_dressing:   3,
    },
    hubs:       { cheese_hub: 22 },
    categories: { dairy: 22, pantry: 34 },
    subtypes:   ["cheese", "boxed_meal", "dressing", "sauce", "condiment"],
    primaryHubsOrdered: ["cheese_hub"],
  },

  // ── Cheese / butter / dairy ─────────────────────────────────────
  kerrygold: {
    canonicals: {
      butter:          14,
      irish_cheese:     6,
      cheddar:          5,
    },
    hubs:       { cheese_hub: 11 },
    categories: { dairy: 25 },
    subtypes:   ["butter", "cheese"],
    primaryHubsOrdered: ["cheese_hub"],
  },
  tillamook: {
    canonicals: {
      cheddar:         16,
      ice_cream:       12,
      yogurt:           8,
      sour_cream:       4,
      butter:           4,
    },
    hubs:       { cheese_hub: 16, yogurt_hub: 8, milk_hub: 4 },
    categories: { dairy: 44 },
    subtypes:   ["cheese", "ice_cream", "yogurt", "butter"],
    primaryHubsOrdered: ["cheese_hub", "yogurt_hub"],
  },
  sargento: {
    canonicals: {
      shredded_cheese:  18,
      sliced_cheese:    14,
      string_cheese:     8,
      mozzarella:        8,
      cheddar:           8,
      provolone:         5,
      parmesan:          4,
    },
    hubs:       { cheese_hub: 65 },
    categories: { dairy: 65 },
    subtypes:   ["cheese"],
    primaryHubsOrdered: ["cheese_hub"],
  },
  philadelphia: {
    canonicals: {
      cream_cheese:    16,
    },
    hubs:       { cheese_hub: 16 },
    categories: { dairy: 16 },
    subtypes:   ["cheese"],
    primaryHubsOrdered: ["cheese_hub"],
  },

  // ── Yogurt ──────────────────────────────────────────────────────
  chobani: {
    canonicals: {
      greek_yogurt:    24,
      yogurt:          12,
      yogurt_drink:     6,
    },
    hubs:       { yogurt_hub: 42 },
    categories: { dairy: 42 },
    subtypes:   ["yogurt"],
    primaryHubsOrdered: ["yogurt_hub"],
  },
  fage: {
    canonicals: {
      greek_yogurt:    14,
    },
    hubs:       { yogurt_hub: 14 },
    categories: { dairy: 14 },
    subtypes:   ["yogurt"],
    primaryHubsOrdered: ["yogurt_hub"],
  },
  oikos: {
    canonicals: {
      greek_yogurt:    16,
    },
    hubs:       { yogurt_hub: 16 },
    categories: { dairy: 16 },
    subtypes:   ["yogurt"],
    primaryHubsOrdered: ["yogurt_hub"],
  },

  // ── Candy / chocolate / inclusions ──────────────────────────────
  // These brands are heavy candy/chocolate manufacturers. When their
  // name appears on a product whose canonical lives OUTSIDE their
  // expertise (e.g. M&M's on a cookie, Reese's on an ice cream),
  // classifyBrandMentions demotes them to "ingredient mention" — the
  // brand is licensed as an INCLUSION, not the actual manufacturer.
  mms: {
    canonicals: {
      candy:               12,
      chocolate_candy:     14,
      peanut_butter_candy:  4,
    },
    hubs:       {},
    categories: { pantry: 30 },
    subtypes:   ["candy"],
    primaryHubsOrdered: [],
  },
  reeses: {
    canonicals: {
      peanut_butter_cup:   12,
      chocolate_candy:      6,
      candy:                4,
    },
    hubs:       {},
    categories: { pantry: 22 },
    subtypes:   ["candy"],
    primaryHubsOrdered: [],
  },
  hersheys: {
    canonicals: {
      chocolate:           18,
      chocolate_candy:     14,
      cocoa_powder:         4,
      chocolate_chips:      5,
    },
    hubs:       {},
    categories: { pantry: 41 },
    subtypes:   ["candy", "chocolate"],
    primaryHubsOrdered: [],
  },
  snickers: {
    canonicals: {
      candy_bar:           8,
      chocolate_candy:     4,
    },
    hubs:       {},
    categories: { pantry: 12 },
    subtypes:   ["candy"],
    primaryHubsOrdered: [],
  },
  oreo: {
    // Oreo IS a real cookie brand (Mondelez) — subtypes=["cookie"]
    // means on a non-cookie SKU (Klondike Oreo, Breyers Oreo) it
    // demotes correctly: subtype mismatch to "ice_cream".
    canonicals: {
      cookie:              10,
      cookies_and_cream:    4,
      sandwich_cookie:      8,
    },
    hubs:       {},
    categories: { pantry: 22 },
    subtypes:   ["cookie"],
    primaryHubsOrdered: [],
  },
  kit_kat: {
    canonicals: {
      candy_bar:           6,
      chocolate_candy:     4,
    },
    hubs:       {},
    categories: { pantry: 10 },
    subtypes:   ["candy"],
    primaryHubsOrdered: [],
  },
  nutella: {
    canonicals: {
      hazelnut_spread:     5,
      chocolate_spread:    3,
    },
    hubs:       {},
    categories: { pantry: 8 },
    subtypes:   ["spread"],
    primaryHubsOrdered: [],
  },

  // ── Cookies / baked snacks ──────────────────────────────────────
  // Real cookie/cracker brands — primary on cookie canonicals.
  chips_ahoy: {
    canonicals: {
      cookie:              16,
      chocolate_chip_cookie: 12,
    },
    hubs:       {},
    categories: { pantry: 28 },
    subtypes:   ["cookie"],
    primaryHubsOrdered: [],
  },
  famous_amos: {
    canonicals: {
      cookie:               6,
      chocolate_chip_cookie: 5,
    },
    hubs:       {},
    categories: { pantry: 11 },
    subtypes:   ["cookie"],
    primaryHubsOrdered: [],
  },
  pepperidge_farm: {
    canonicals: {
      cookie:               8,
      cracker:              6,
      bread:                6,
      goldfish:             4,
    },
    hubs:       { bread_hub: 6 },
    categories: { pantry: 24 },
    subtypes:   ["cookie", "cracker", "bread"],
    primaryHubsOrdered: ["bread_hub"],
  },
  keebler: {
    canonicals: {
      cookie:               8,
      cracker:              6,
    },
    hubs:       {},
    categories: { pantry: 14 },
    subtypes:   ["cookie", "cracker"],
    primaryHubsOrdered: [],
  },

  // ── Ice cream ───────────────────────────────────────────────────
  // Real ice-cream manufacturers — primary on ice_cream canonicals
  // even when M&M's or Oreo licensing surfaces in the productName.
  ben_jerrys: {
    canonicals: {
      ice_cream:           24,
      frozen_yogurt:        4,
    },
    hubs:       {},
    categories: { dairy: 28 },
    subtypes:   ["ice_cream"],
    primaryHubsOrdered: [],
  },
  haagen_dazs: {
    canonicals: {
      ice_cream:           20,
    },
    hubs:       {},
    categories: { dairy: 20 },
    subtypes:   ["ice_cream"],
    primaryHubsOrdered: [],
  },
  breyers: {
    canonicals: {
      ice_cream:           18,
    },
    hubs:       {},
    categories: { dairy: 18 },
    subtypes:   ["ice_cream"],
    primaryHubsOrdered: [],
  },
  klondike: {
    canonicals: {
      ice_cream:           10,
      ice_cream_bar:        8,
    },
    hubs:       {},
    categories: { dairy: 18 },
    subtypes:   ["ice_cream"],
    primaryHubsOrdered: [],
  },

  // ── Multi-line conglomerate / shelf-stable ──────────────────────
  // These brands span so many categories that picking a primary hub
  // is meaningless; their value here is BRAND-KNOWNNESS as a
  // tie-breaker. Empty primaryHubsOrdered signals "no specialty —
  // resolve by canonical match alone."
  general_mills: {
    canonicals: {
      cereal:          12,
      flour:            6,
      yogurt:           8,
    },
    hubs:       { flour_hub: 6, yogurt_hub: 8 },
    categories: { pantry: 18, dairy: 8 },
    primaryHubsOrdered: [],
  },
  nestle: {
    canonicals: {
      chocolate:       10,
      ice_cream:        8,
      coffee:           6,
      water:            5,
    },
    hubs:       { milk_hub: 8 },
    categories: { pantry: 21, dairy: 8 },
    primaryHubsOrdered: [],
  },

  // ── Regional store house-brands ─────────────────────────────────
  // Hand-curated from observed shelf presence, not extracted from
  // anyone's catalog. The `subtypes` array is the AUTHORITATIVE
  // CATEGORICAL CLAIM — "this brand private-labels these subtypes" —
  // and powers the Tier-1.5 subtype soft-fallback in
  // brandsForCanonical(). The `regions` array is geographic scope
  // for the same picker's region filter. Other fields are editorial
  // metadata for future use (brands table, partnership routing).
  //
  // Add new entries here when you observe a regional private label
  // in the wild. Don't enumerate every SKU — the subtype claim does
  // the work. Add a SKU only if it's a "this brand's signature
  // product" worth weighting above the subtype baseline.
  kroger: {
    parentCompany:   "The Kroger Co.",
    scope:           "regional",
    tier:            "mainstream",
    isPrivateLabel:  true,
    privateLabelFor: ["Kroger"],
    // Kroger banner footprint — central + south. NOT Missouri
    // (Kroger pulled out of MO in 2000), NOT West Coast (those
    // are sister banners Ralphs / Smith's / Fred Meyer / QFC),
    // NOT Northeast (Wegmans / Stop & Shop territory). The
    // Co. owns ~30 banners; this entry is the Kroger-named
    // banner only — Ralphs/Smith's/Dillons/etc. need their own
    // slugs if/when they show up in pickers.
    regions: [
      "us-oh", "us-ky", "us-tn", "us-in", "us-mi", "us-wv", "us-va",
      "us-ga", "us-al", "us-ar", "us-la", "us-ms", "us-tx-houston",
    ],
    primary_states: ["OH", "KY", "TN", "IN", "MI", "WV", "VA", "GA", "AL", "AR", "LA", "MS"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes: [
      // dairy
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      "sour_cream",
      // pantry
      "spice", "seasoning", "sauce", "dressing", "condiment", "spread",
      "sweetener", "baking", "boxed_meal", "bread", "cracker", "cookie",
      "pasta", "rice", "cereal", "oil",
      // snacks / beverages
      "popcorn", "candy", "chips", "snacks", "water", "juice", "soda",
      "beverage", "coffee", "tea",
      // frozen
      "frozen_veg", "canned_veg", "frozen_meal",
      // meat (frozen / packaged)
      "chicken_cut", "beef_cut", "pork_cut", "bacon", "sausage",
    ],
    primaryHubsOrdered: [],
  },

  schnucks: {
    parentCompany:   "Schnuck Markets, Inc.",
    scope:           "regional",
    tier:            "mainstream",
    isPrivateLabel:  true,
    privateLabelFor: ["Schnucks", "Logli Supermarkets"],
    // METRO-LEVEL precision (not statewide). Schnucks is the St.
    // Louis metro area + central MO + a few IL cities — NOT a MO
    // statewide chain. Tagging `us-mo` would incorrectly surface
    // Schnucks for Springfield/Joplin/Kansas City users where no
    // Schnucks store exists. The curated-regions gate uses this
    // list to override any broader Tier-5 tag from Haiku.
    regions: [
      "us-mo-st_louis",
      "us-il-st_louis",        // Metro East
      "us-mo-jefferson_city",
      "us-mo-columbia",
      "us-il-bloomington",
      "us-il-edwardsville",
      "us-il-rockford",
      "us-in-evansville",
    ],
    primary_states: ["MO", "IL", "IN"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes: [
      // dairy
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      // pantry / baking
      "spice", "seasoning", "sauce", "dressing", "condiment", "spread",
      "sweetener", "baking", "boxed_meal", "bread", "cracker", "cookie",
      // snacks / beverages
      "popcorn", "candy", "chips", "snacks", "water", "juice", "soda",
      "beverage", "coffee", "tea",
      // frozen / canned
      "frozen_veg", "canned_veg", "frozen_meal",
      // fresh produce — Schnucks Selects organic produce line
      "fruit_fresh", "vegetable_fresh", "herb_fresh",
      // other
      "margarine", "oil",
    ],
    primaryHubsOrdered: [],
  },

  best_choice: {
    parentCompany:   "Associated Wholesale Grocers (AWG)",
    scope:           "regional",
    tier:            "value",
    isPrivateLabel:  true,
    privateLabelFor: ["Price Cutter", "Country Mart", "Pyramid Foods", "Apple Market"],
    regions:         ["us-mo", "us-ar", "us-ks", "us-ne", "us-ok"],
    canonicals: {},
    hubs:       {},
    categories: {},
    // Broad private-label coverage. Order isn't significant.
    subtypes: [
      // dairy
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      // pantry / baking
      "spice", "seasoning", "sauce", "dressing", "condiment", "spread",
      "sweetener", "baking", "boxed_meal", "bread", "cracker", "cookie",
      // snacks
      "popcorn", "candy", "chips", "snacks",
      // beverages
      "water", "juice", "soda", "beverage", "coffee", "tea",
      // frozen / canned / produce-shelf
      "frozen_veg", "canned_veg", "frozen_meal",
      // other private-label ranges
      "margarine", "oil",
    ],
    primaryHubsOrdered: [],
  },

  // ── Walmart house-brand ecosystem ───────────────────────────────
  // Walmart maintains multiple distinct private-label sub-brands.
  // Each gets its own entry because their subtype scope and tier
  // positioning differ. National distribution → regions: ["us"].

  great_value: {
    parentCompany:   "Walmart Inc.",
    scope:           "national",
    tier:            "value",
    isPrivateLabel:  true,
    privateLabelFor: ["Walmart", "Walmart Neighborhood Market"],
    regions:         ["us"],
    primary_states:  [],   // national chain, no state concentration
    canonicals: {},
    hubs:       {},
    categories: {},
    // Broadest mainstream private label in US grocery — almost
    // every grocery subtype has a Great Value version.
    subtypes: [
      // dairy
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      "sour_cream", "margarine",
      // pantry — staples
      "spice", "seasoning", "sauce", "dressing", "condiment", "spread",
      "sweetener", "baking", "boxed_meal", "bread", "cracker", "cookie",
      "pasta", "rice", "cereal", "oil", "vinegar",
      // pantry — preserved
      "jam", "jelly", "salsa", "canned_veg", "fish", "seafood",
      // snacks
      "popcorn", "candy", "chips", "snacks", "snack_meat", "tortilla",
      // beverages
      "water", "juice", "soda", "beverage", "coffee", "tea",
      // frozen
      "frozen_veg", "frozen_meal",
      // meat / poultry / deli (mostly frozen + packaged at Walmart)
      "chicken_cut", "beef_cut", "pork_cut", "turkey_cut",
      "bacon", "nugget", "wing", "deli", "sausage",
      "hot_dog", "hamburger", "frank",
      // eggs (their grade-A eggs carton)
      "egg",
    ],
    primaryHubsOrdered: [],
  },

  marketside: {
    parentCompany:   "Walmart Inc.",
    scope:           "national",
    tier:            "mainstream",
    isPrivateLabel:  true,
    privateLabelFor: ["Walmart", "Walmart Neighborhood Market"],
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    // Walmart's fresh / deli / bakery sub-brand — narrower scope
    // than Great Value, focused on prepared and short-shelf items.
    // Includes Marketside-branded sliced apples, sliced fruit
    // bowls, packaged salads, fresh herbs — that's why
    // fruit_fresh/vegetable_fresh/herb_fresh are claimed here.
    subtypes: [
      "bread", "deli", "sausage", "boxed_meal", "frozen_meal",
      "hummus", "dressing", "sauce", "spread",
      "pasta", "salsa", "frozen_dumpling", "naan", "tortilla", "pizza",
      "fruit_fresh", "vegetable_fresh", "herb_fresh",
    ],
    primaryHubsOrdered: [],
  },

  // ── Fresh-produce private labels & specialty brands ───────────
  // Most produce is sold loose without brand. The brands that DO
  // appear on fresh produce — packaged sliced fruits, organic
  // greens, branded salads — are tracked here so the picker shows
  // SOMETHING for produce canonicals (apple, lettuce, etc.) instead
  // of an empty list.

  kirkland_signature: {
    parentCompany:   "Costco Wholesale",
    scope:           "national",
    tier:            "premium",
    isPrivateLabel:  true,
    privateLabelFor: ["Costco"],
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    // Costco's house brand — extremely broad, premium positioning.
    subtypes: [
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      "spice", "seasoning", "sauce", "dressing", "condiment",
      "spread", "sweetener", "baking", "bread", "cracker", "cookie",
      "pasta", "rice", "cereal", "oil", "vinegar",
      "popcorn", "candy", "chips", "snacks", "snack_meat",
      "water", "juice", "coffee", "tea", "beverage",
      "frozen_veg", "canned_veg", "frozen_meal",
      "chicken_cut", "beef_cut", "pork_cut", "bacon", "sausage", "deli",
      "fish", "seafood",
      "fruit_fresh", "vegetable_fresh", "herb_fresh",
    ],
    primaryHubsOrdered: [],
  },

  hy_vee_brand: {
    displayName:     "Hy-Vee Brand",
    parentCompany:   "Hy-Vee, Inc.",
    scope:           "regional",
    tier:            "mainstream",
    isPrivateLabel:  true,
    privateLabelFor: ["Hy-Vee", "Hy-Vee Drugstore"],
    // Hy-Vee operates in 8 Midwestern states.
    regions: [
      "us-ia", "us-mo", "us-il", "us-ks", "us-ne", "us-mn",
      "us-sd", "us-wi",
    ],
    primary_states: ["IA", "MO", "IL", "KS", "NE", "MN", "SD", "WI"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes: [
      "cheese", "milk", "cream", "butter", "yogurt", "ice_cream",
      "spice", "seasoning", "sauce", "dressing", "condiment",
      "spread", "sweetener", "baking", "bread", "cracker", "cookie",
      "pasta", "rice", "cereal", "oil",
      "popcorn", "candy", "chips", "snacks",
      "water", "juice", "soda", "beverage", "coffee", "tea",
      "frozen_veg", "canned_veg", "frozen_meal",
      "chicken_cut", "beef_cut", "pork_cut", "bacon", "sausage",
      "fruit_fresh", "vegetable_fresh", "herb_fresh",
    ],
    primaryHubsOrdered: [],
  },

  // Specialty fresh-produce brands — these companies ARE the
  // brand on the package for sliced/packaged fresh produce.
  crunch_pak: {
    parentCompany:   "Crunch Pak Inc.",
    scope:           "national",
    tier:            "mainstream",
    isPrivateLabel:  false,
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes:        ["fruit_fresh"],   // sliced apples, fruit cups
    primaryHubsOrdered: [],
  },

  stemilt: {
    parentCompany:   "Stemilt Growers",
    scope:           "national",
    tier:            "mainstream",
    isPrivateLabel:  false,
    regions:         ["us"],
    primary_states:  ["WA", "OR", "ID"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes:        ["fruit_fresh"],   // apples, pears, cherries
    primaryHubsOrdered: [],
  },

  driscolls: {
    displayName:     "Driscoll's",
    parentCompany:   "Driscoll's, Inc.",
    scope:           "international",
    tier:            "premium",
    isPrivateLabel:  false,
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes:        ["fruit_fresh"],   // strawberries, blueberries, raspberries, blackberries
    primaryHubsOrdered: [],
  },

  dole: {
    parentCompany:   "Dole Food Company",
    scope:           "international",
    tier:            "mainstream",
    isPrivateLabel:  false,
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes:        ["fruit_fresh", "vegetable_fresh", "frozen_veg", "canned_veg", "juice"],
    primaryHubsOrdered: [],
  },

  earthbound_farm: {
    displayName:     "Earthbound Farm",
    parentCompany:   "Taylor Farms",
    scope:           "national",
    tier:            "premium",
    isPrivateLabel:  false,
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    subtypes:        ["vegetable_fresh", "herb_fresh"],   // organic salads, herbs
    primaryHubsOrdered: [],
  },

  sams_choice: {
    parentCompany:   "Walmart Inc.",
    scope:           "national",
    tier:            "premium",
    isPrivateLabel:  true,
    privateLabelFor: ["Walmart"],
    regions:         ["us"],
    canonicals: {},
    hubs:       {},
    categories: {},
    // Walmart's premium private label — narrower than Great Value,
    // upmarket positioning. Heavy on beverages, snacks, premium
    // pantry items.
    subtypes: [
      "soda", "water", "beverage", "juice",
      "cookie", "candy", "chips", "popcorn", "snacks",
      "sauce", "condiment", "spread", "pasta",
    ],
    primaryHubsOrdered: [],
  },
};

// USDA-derived expertise — auto-generated by
// scripts/derive_brand_expertise.js from the USDA Branded Foods
// dataset. The committed stub at brandExpertiseDerived.js exports
// an empty object until the script is run; once generated, this
// import surfaces a histogram covering hundreds-to-thousands of
// brands the curated seed below doesn't reach.
//
// Curated entries in BRAND_EXPERTISE add ON TOP of derived counts
// (see mergeRecords + expertiseFor below) so hand-tuned tweaks
// always win on conflict, but coverage extends to whatever USDA
// has cataloged.
import { BRAND_EXPERTISE_FROM_USDA as DERIVED } from "./brandExpertiseDerived";
import { KNOWN_BRANDS } from "./knownBrands";
import { findIngredient } from "./ingredients";
import { typeIdForCanonical } from "./foodTypes";
import { subtypeForTypeId, subtypeForHub } from "./subtypeMap";

// Merge derived + curated counts for a brand slug. Curated wins on
// every key that's present in BRAND_EXPERTISE; derived fills in
// canonicals/hubs/categories the curated entry doesn't cover.
//
// SUBTYPE MERGE RULE — curated subtypes are AUTHORITATIVE INTENT,
// not observation. When a brand has explicit curated subtypes, use
// that list VERBATIM and ignore USDA-derived subtypes. Reason:
// USDA's observational data correctly shows that M&M's appears on
// some cookie SKUs (real licensed inclusion products) — but the
// curated entry encodes "M&M's IS a candy brand" as intent. Unioning
// would dilute the demotion gate: M&M's-on-cookie would stop being
// classified as an inclusion-licensor because USDA observed 3 cookie-
// branded M&M's SKUs.
//
// USDA-only brands (no curated entry) keep their derived subtypes
// — that's the whole point of the derivation pipeline.
function mergeRecords(curated, derived) {
  if (!curated && !derived) return null;
  if (!derived) return curated;
  if (!curated) return derived;
  // Subtype rule: prefer curated when present, else fall back to
  // derived. Don't union — see comment above.
  const subtypes =
    (Array.isArray(curated.subtypes) && curated.subtypes.length > 0)
      ? curated.subtypes
      : (Array.isArray(derived.subtypes) ? derived.subtypes : []);
  return {
    canonicals: { ...derived.canonicals, ...curated.canonicals },
    hubs:       { ...derived.hubs,       ...curated.hubs       },
    categories: { ...derived.categories, ...curated.categories },
    subtypes,
    primaryHubsOrdered:
      curated.primaryHubsOrdered || derived.primaryHubsOrdered || [],
  };
}

// Look up the expertise record for a brand display string. Slugifies
// internally so callers can pass "Slim Jim" or "slim jim" — both
// resolve to the same record. Merges curated + USDA-derived data so
// brands not in the curated seed still get classification weights
// from the broader USDA Branded Foods dataset.
export function expertiseFor(brandDisplay) {
  const slug = brandSlugify(brandDisplay);
  if (!slug) return null;
  return mergeRecords(BRAND_EXPERTISE[slug] || null, DERIVED[slug] || null);
}

// Inverse of expertiseFor — given a canonical id, return brands whose
// expertise records list that canonical. Used by surfaces that want
// to PRESENT a brand picker for a known canonical (the placement
// wizard's size step today; brand-rename pickers tomorrow).
//
// Strictly canonical-keyed by design. Hub / category fallbacks were
// considered but produce overly broad dumps — a "soda" canonical with
// hub fallback would surface every beverage brand, including ones
// that have never made a soda. Keeping the lookup canonical-only
// means the list is always defensibly "brands we believe make THIS
// product."
//
// Three sources merged, in priority order:
//   1. Curated `BRAND_EXPERTISE` — explicit canonical counts. Highest
//      weight because they're hand-tuned ("Slim Jim → beef_stick=30").
//   2. USDA-derived `BRAND_EXPERTISE_FROM_USDA` — same shape, populated
//      by the regen script. Today's checked-in file leaves canonicals
//      empty (the derivation seeds hubs/categories/subtypes only) but
//      the merge stays in place so future re-runs surface automatically.
//   3. `KNOWN_BRANDS.canonicalHint` — every entry whose hint matches
//      contributes a small baseline. Catches single-canonical brands
//      the expertise table doesn't bother to seed (Pepsi → soda lives
//      here, not in BRAND_EXPERTISE).
//
// Returns up to `limit` entries sorted by combined score, each:
//   { display, slug, score }
// `display` is the prettified brand name (resolved via knownBrands.js
// when possible, slug-Title-Case fallback otherwise).
// brandsForCanonical — five-tier merge:
//
//   Tier 1: BRAND_EXPERTISE        (curated; ~30 hand-tuned brands)
//   Tier 2: BRAND_EXPERTISE_FROM_USDA (derived from USDA Branded Foods)
//   Tier 3: KNOWN_BRANDS canonicalHint (Pepsi→soda, Coca-Cola→soda, …)
//   Tier 4: live observations from manual_corrections (user picks)
//   Tier 5: AI-suggested common brands (Haiku-populated info.brands.common)
//
// Tiers 1-3 are static (require no caller setup). Tiers 4 + 5 are
// caller-supplied via opts:
//
//   observations: array of { display | slug, count } from
//                 useBrandObservations(canonicalId)
//   info:         the ingredient_info merged record from
//                 getInfo(canonicalId) — we read info.brands.common
//   region:       user's shopping locality (e.g. "us-midwest-chicago")
//                 — Tier 5 brands carry { regions, scope } metadata
//                 from the Haiku schema; non-matching brands are
//                 filtered. Brands without geo metadata pass through.
//
// When opts is empty, the function returns the static 3-tier merge
// (back-compat for every existing caller).
export function brandsForCanonical(canonicalId, opts = {}) {
  if (!canonicalId) return [];
  const {
    limit = 12,
    observations = null,
    info = null,
    region = null,
    // Map<slug, row> or array of brand_identity rows. Pass from
    // useBrandIdentity().bySlug at the call-site so the picker
    // surfaces AI-populated brand identities alongside curated.
    brandIdentities = null,
    // Active store context ("Costco") — house brands whose
    // privateLabelFor matches get a boost + storeMatch tag so
    // pickers can render an "AT COSTCO" section. Null outside a
    // trip → zero behavior change.
    store = null,
  } = opts;

  // Debug flag — enable from DevTools console:
  //   window.__BRAND_DEBUG__ = true
  // then open any picker. Disable with:
  //   window.__BRAND_DEBUG__ = false
  // Logs per-canonical resolution + per-Tier-5-entry pass/filter
  // decisions so you can see exactly why a brand surfaced or didn't.
  const DBG = typeof window !== "undefined" && window.__BRAND_DEBUG__ === true;
  const dbgLog = DBG ? [] : null;
  const dbgFiltered = DBG ? [] : null;
  const bySlug = new Map();
  const displayBySlug = new Map();
  const metaBySlug = new Map(); // slug → { stores, regions, scope, tier } for Tier-5 enrichments
  const add = (slug, score, display, meta = null) => {
    if (!slug || !(score > 0)) return;
    bySlug.set(slug, (bySlug.get(slug) || 0) + score);
    if (display && !displayBySlug.has(slug)) displayBySlug.set(slug, display);
    if (meta && !metaBySlug.has(slug)) metaBySlug.set(slug, meta);
  };

  // Tier 1: curated.
  for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
    add(slug, Number(rec?.canonicals?.[canonicalId] || 0));
  }
  // Tier 2: derived (USDA).
  for (const [slug, rec] of Object.entries(DERIVED)) {
    add(slug, Number(rec?.canonicals?.[canonicalId] || 0));
  }
  // Tier 1.5 (curated subtype claim) + Tier 2.5 (derived subtype):
  // when a brand declares a categorical claim via subtypes[] but
  // doesn't enumerate every canonical (the private-label pattern —
  // Best Choice, Schnucks Selects, Hy-Vee brand, etc.), surface it
  // for any canonical whose subtype matches the claim. Sits between
  // Tier 5 AI baseline (5) and Tier 4 observations (8 × n) so
  // curated intent shows up reliably without overriding hand-tuned
  // canonical counts. Region filter applied so a Springfield user
  // doesn't see Schnucks Selects outranked by an Ozark-only brand.
  // Subtype resolution chain. Each step falls back to the next
  // when null:
  //   1. explicit subtype on the canonical itself
  //   2. subtype on the parent hub (parmesan → cheese_hub → "cheese")
  //   3. WWEIA type → subtype bridge (legacy / OFF-derived path)
  // Without this chain, half the dairy registry plus most spices
  // resolved to null subtype and the soft-fallback never fired —
  // which is why Best Choice didn't surface for nutmeg, cheddar,
  // milk, etc. even though it claims those subtypes.
  const ing = findIngredient(canonicalId);
  const subtype =
    (ing && ing.subtype) ||
    subtypeForHub(ing?.parentId) ||
    subtypeForTypeId(typeIdForCanonical(ing)) ||
    null;
  if (DBG) {
    dbgLog.push({
      stage: "resolve",
      canonicalId,
      region,
      parentId: ing?.parentId || null,
      explicitSubtype: ing?.subtype || null,
      hubSubtype: subtypeForHub(ing?.parentId) || null,
      wweiaSubtype: subtypeForTypeId(typeIdForCanonical(ing)) || null,
      resolvedSubtype: subtype,
      category: ing?.category || null,
      tier5_entries_in_info: info?.brands?.common?.length || 0,
    });
  }
  const subtypeNorm = normSubtype(subtype);
  // Specialty-canonical short-circuit: when the canonical lives in
  // a "Specialty" subcategory (gjetost in Scandinavian / Specialty,
  // roquefort in Blue, beaufort in Alpine, etc.), the subtype-claim
  // tiers DO NOT fire. Subtype-claim is "this brand makes cheese"
  // — fine for commodity cheeses where every grocery private label
  // has a version (Great Value cheddar exists), wrong for specialty
  // cheeses where most private labels don't carry a version (Great
  // Value gjetost does NOT exist). For specialty canonicals, only
  // per-canonical evidence (Tier 1, 2, 3, 4, 5) surfaces brands.
  // Empty picker beats wrong picker.
  const isSpecialty =
    ing?.subcategory && SPECIALTY_SUBCATEGORIES.has(ing.subcategory);
  if (subtypeNorm && !isSpecialty) {
    for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
      const set = subtypeSetFrom(rec?.subtypes);
      if (!set || !set.has(subtypeNorm)) continue;
      if (region && !brandMatchesRegionLocal(rec, region)) continue;
      add(slug, 7);
    }
    for (const [slug, rec] of Object.entries(DERIVED)) {
      const set = subtypeSetFrom(rec?.subtypes);
      if (!set || !set.has(subtypeNorm)) continue;
      if (region && !brandMatchesRegionLocal(rec, region)) continue;
      add(slug, 4);
    }
    // Tier 1.7: runtime brand_identity rows (Haiku-populated via
    // enrich-brand-identity edge fn). Same subtype-claim logic as
    // curated, but score scales with the AI's self-reported
    // confidence.
    if (brandIdentities) {
      const iter =
        typeof brandIdentities.values === "function"
          ? brandIdentities.values()
          : Array.isArray(brandIdentities)
          ? brandIdentities
          : [];
      for (const rec of iter) {
        if (!rec || typeof rec !== "object") continue;
        const slug = rec.slug || (rec.display_name ? brandSlugify(rec.display_name) : null);
        if (!slug) continue;
        const set = subtypeSetFrom(rec.subtypes);
        if (!set || !set.has(subtypeNorm)) continue;
        if (region && !brandMatchesRegionLocal(rec, region)) continue;
        const conf = Number(rec.confidence) || 50;
        // 90+ → 6, 70-89 → 4, 50-69 → 2, <50 → 1
        const score = conf >= 90 ? 6 : conf >= 70 ? 4 : conf >= 50 ? 2 : 1;
        add(slug, score, rec.display_name || null);
      }
    }
  }
  // Tier 3: KNOWN_BRANDS hint.
  for (const entry of KNOWN_BRANDS) {
    if (entry.canonicalHint === canonicalId) {
      add(brandSlugify(entry.display), 1, entry.display);
    }
  }
  // Tier 4: live observations from manual_corrections AND from
  // pantry_items via useBrandsFromPantry. The two hooks return
  // different shapes ({ display, slug, count } vs { brand,
  // correction_count }) so derive slug + display from any of them.
  if (Array.isArray(observations)) {
    for (const obs of observations) {
      if (!obs) continue;
      const display = obs.display || obs.brand || null;
      const slug = obs.slug || (display ? brandSlugify(display) : null);
      const count = Number(obs.count || obs.correction_count || 1);
      if (!slug || !(count > 0)) continue;
      add(slug, 8 * count, display);
    }
  }
  // Tier 5: AI-suggested common brands. Two payload shapes supported:
  //   Legacy:    info.brands.common = ["Barilla", "Ronzoni", …]
  //   Extended:  info.brands.common = [{ name, scope, regions, stores, tier }, …]
  // Region filter only applies to extended-shape entries — legacy
  // strings carry no region metadata and pass through.
  const aiBrands = info?.brands?.common;
  if (Array.isArray(aiBrands)) {
    for (const entry of aiBrands) {
      let display, meta;
      if (typeof entry === "string") {
        display = entry.trim();
        meta = null;
      } else if (entry && typeof entry === "object" && typeof entry.name === "string") {
        display = entry.name.trim();
        meta = {
          scope:   entry.scope || null,
          regions: Array.isArray(entry.regions) ? entry.regions : null,
          stores:  Array.isArray(entry.stores) ? entry.stores : null,
          tier:    entry.tier || null,
          variants: Array.isArray(entry.variants) ? entry.variants : null,
        };
        if (region && !brandMatchesRegionLocal(meta, region)) continue;
      } else {
        continue;
      }
      if (!display) continue;
      // Subtype gate: when EITHER the curated BRAND_EXPERTISE entry
      // OR the runtime brand_identity row has a subtypes[] claim
      // that doesn't include this canonical's subtype, drop the
      // entry. Best Choice on cilantro (subtype=herb_fresh, not in
      // best_choice.subtypes) gets filtered here — even though
      // Haiku put it in info.brands.common during the regional pass.
      // Authoritative claims (curated or brand_identity) win over
      // AI overreach. Brands without a claim entry, or canonicals
      // without a resolvable subtype, pass through.
      const aiSlug = brandSlugify(display);
      const curated = BRAND_EXPERTISE[aiSlug];
      const derived = DERIVED[aiSlug];
      const identity =
        brandIdentities && typeof brandIdentities.get === "function"
          ? brandIdentities.get(aiSlug)
          : null;
      // Union curated + USDA-derived + runtime identity claims for
      // the gate. Three-source union: any source can GRANT a
      // subtype; the entry passes if ANY source claims it.
      const curatedSet = subtypeSetFrom(curated?.subtypes);
      const derivedSet = subtypeSetFrom(derived?.subtypes);
      const identitySet = subtypeSetFrom(identity?.subtypes);
      let claimSet = null;
      if (curatedSet || derivedSet || identitySet) {
        claimSet = new Set();
        if (curatedSet)  for (const s of curatedSet)  claimSet.add(s);
        if (derivedSet)  for (const s of derivedSet)  claimSet.add(s);
        if (identitySet) for (const s of identitySet) claimSet.add(s);
      }
      // Compute category claim set up front so strict mode and
      // category gate share the same data and don't compete.
      const canonicalCategory =
        (ing?.category || "").toLowerCase().trim() || null;
      const categoryClaimSet = new Set();
      for (const obj of [curated?.categories, derived?.categories, identity?.categories]) {
        if (obj && typeof obj === "object" && !Array.isArray(obj)) {
          for (const k of Object.keys(obj)) {
            const v = Number(obj[k]);
            if (v > 0) categoryClaimSet.add(String(k).toLowerCase().trim());
          }
        }
      }

      // Permissive subtype gate: claim exists and doesn't match → filter.
      if (subtypeNorm && claimSet && !claimSet.has(subtypeNorm)) {
        if (DBG) dbgFiltered.push({
          display, slug: aiSlug,
          reason: "subtype_mismatch",
          canonicalSubtype: subtypeNorm,
          canonicalCategory,
          brandClaim: [...claimSet],
        });
        continue;
      }
      // Permissive category gate: category claim exists and doesn't
      // match → filter. Catches Pepsi (categories=pantry,beverage)
      // on a produce canonical even when subtype data is sparse.
      if (canonicalCategory && categoryClaimSet.size > 0 && !categoryClaimSet.has(canonicalCategory)) {
        if (DBG) dbgFiltered.push({
          display, slug: aiSlug,
          reason: "category_mismatch",
          canonicalCategory,
          canonicalSubtype: subtypeNorm,
          brandCategoryClaim: [...categoryClaimSet],
        });
        continue;
      }
      // Strict mode: brand has NO claim of any kind (no subtype,
      // no category) AND canonical is in a narrow context (specific
      // subtype like fruit_fresh, OR specific category like produce).
      // For narrow contexts, no-claim defaults to "doesn't apply"
      // — most brands genuinely don't make fresh produce, and we
      // don't want Haiku word-association hallucinations
      // (Coca-Cola for "Anaheim Pepper", Frito-Lay for basil) to
      // fall through unfiltered. For broader contexts (dairy,
      // pantry, beverage) no-claim is "we don't know yet" rather
      // than "doesn't apply" — permissive.
      const isNarrowSubtype = subtypeNorm && NARROW_SUBTYPES.has(subtypeNorm);
      const isNarrowCategory = canonicalCategory && NARROW_CATEGORIES.has(canonicalCategory);
      const hasAnyClaim = !!claimSet || categoryClaimSet.size > 0;
      if (!hasAnyClaim && (isNarrowSubtype || isNarrowCategory)) {
        if (DBG) dbgFiltered.push({
          display, slug: aiSlug,
          reason: isNarrowCategory ? "narrow_category_no_claim" : "narrow_subtype_no_claim",
          canonicalSubtype: subtypeNorm,
          canonicalCategory,
        });
        continue;
      }
      // Curated-regions override gate. When the brand has an
      // authoritative regions[] from curated OR identity AND those
      // regions don't include the user's region, drop the entry —
      // even if Haiku put it in info.brands.common with a more
      // permissive region tag. Caught the "Schnucks shows up in
      // Springfield, MO" case: Haiku tagged Schnucks `us-mo`
      // (statewide) which prefix-matches us-mo-springfield, but
      // curated knows Schnucks is St. Louis-area only with
      // regions: [us-mo-st_louis, us-il-st_louis, ...]. Curator
      // wins.
      if (region) {
        const curatedRegions = Array.isArray(curated?.regions) && curated.regions.length > 0
          ? curated.regions
          : null;
        const derivedRegions = Array.isArray(derived?.regions) && derived.regions.length > 0
          ? derived.regions
          : null;
        const identityRegions = Array.isArray(identity?.regions) && identity.regions.length > 0
          ? identity.regions
          : null;
        const authoritativeRegions = curatedRegions || identityRegions || derivedRegions;
        if (authoritativeRegions) {
          const recForCheck = {
            scope: curated?.scope || identity?.scope || derived?.scope || null,
            regions: authoritativeRegions,
          };
          if (!brandMatchesRegionLocal(recForCheck, region)) continue;
        }
      }
      // Region-matching brands score slightly higher than no-region
      // brands so locality preference rises in the picker without
      // overriding curated / observed.
      const regionBonus = (region && meta?.regions?.length) ? 1 : 0;
      add(aiSlug, 5 + regionBonus, display, meta);
      if (DBG) dbgFiltered.push({
        display, slug: aiSlug,
        reason: "PASSED_TIER5",
        score: 5 + regionBonus,
      });
    }
  }
  // STORE TIER — "shopping at Costco" boosts that store's house
  // brands (privateLabelFor) so they top the picker AT their store.
  // +8, and it STACKS with Tier 1.5's +7 (both walk BRAND_EXPERTISE
  // with the same gates), so a house brand lands at 15 at its store
  // — above Tier 5 AI (5-6) and the bare subtype claim (7), but
  // still below a twice-observed personal brand (8×2=16): the
  // user's own history beats store priors. (+10 here made the stack
  // 17 and broke that invariant — caught in adversarial verify.)
  // Gated on the SAME subtype/specialty checks as Tier 1.5: a house
  // brand only surfaces where its subtype claim covers this
  // canonical, and specialty canonicals skip the tier entirely (no
  // Kirkland gjetost — empty picker beats wrong picker).
  const storeMatchSlugs = new Set();
  if (store && subtypeNorm && !isSpecialty) {
    for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
      if (!brandServesStore(rec, store)) continue;
      const set = subtypeSetFrom(rec?.subtypes);
      if (!set || !set.has(subtypeNorm)) continue;
      if (region && !brandMatchesRegionLocal(rec, region)) continue;
      add(slug, 8, rec?.displayName || null);
      storeMatchSlugs.add(slug);
    }
  }

  // Drop any "brand" whose slug matches the canonical's own slug
  // (e.g. brand="Gochugaru" surfacing on canonical=gochugaru). This
  // fires almost exclusively from Tier 5 — Haiku occasionally puts
  // the canonical's own name into info.brands.common when the
  // product is generic and there's no real brand to point at, then
  // the picker reads back the canonical name as if it were a brand.
  // Curated escape: when BRAND_EXPERTISE explicitly maps this slug
  // to this canonical (the Spam/Coca-Cola case where the canonical
  // is named after the brand), the entry stays.
  const curatedEscape = (slug) =>
    Number(BRAND_EXPERTISE?.[slug]?.canonicals?.[canonicalId] || 0) > 0;
  for (const slug of [...bySlug.keys()]) {
    if (slug === canonicalId && !curatedEscape(slug)) {
      bySlug.delete(slug);
      displayBySlug.delete(slug);
      metaBySlug.delete(slug);
    }
  }
  const final = [...bySlug.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([slug, score]) => {
      const meta = metaBySlug.get(slug) || {};
      return {
        slug,
        score,
        display: displayBySlug.get(slug) || slugToBrandDisplay(slug),
        // Surface the meta back to callers so the BrandStep can show
        // "regional" / "premium" badges, expand variant pickers, and
        // route receipts to the right store. null when the brand only
        // came from string-shape entries / static tiers.
        scope:    meta.scope || null,
        regions:  meta.regions || null,
        stores:   meta.stores || null,
        tier:     meta.tier || null,
        variants: meta.variants || null,
        // True when the active store's house-brand tier fired for
        // this slug — pickers render these under an "AT {STORE}"
        // section kicker.
        storeMatch: storeMatchSlugs.has(slug),
      };
    });

  if (DBG) {
    /* eslint-disable no-console */
    console.groupCollapsed(`🔍 brandsForCanonical(${canonicalId}) — region=${region || "—"}`);
    console.log("RESOLUTION:", dbgLog[0]);
    console.log("\nALL TIER 5 ENTRIES (info.brands.common):");
    if (dbgFiltered.length === 0) {
      console.log("  (none — info.brands.common was empty or absent)");
    } else {
      console.table(dbgFiltered.map(r => ({
        display:  r.display,
        result:   r.reason,
        canonical_subtype: r.canonicalSubtype || "",
        canonical_category: r.canonicalCategory || "",
        brand_subtype_claim: r.brandClaim ? r.brandClaim.join(",") : "",
        brand_category_claim: r.brandCategoryClaim ? r.brandCategoryClaim.join(",") : "",
        score: r.score ?? "",
      })));
    }
    console.log("\nFINAL PICKER OUTPUT (sorted, top 12):");
    console.table(final.map(b => ({
      slug: b.slug,
      display: b.display,
      score: b.score,
      regions: (b.regions || []).join(","),
    })));
    console.groupEnd();
    /* eslint-enable no-console */
  }

  return final;
}

// Store-name normalization + matching. Case/punctuation-insensitive
// token-subset match so free-text "Costco Wholesale" matches the
// curated privateLabelFor entry "Costco" (and vice versa). Exported
// for storeCatalog.js and any picker that needs the same comparison.
export function normalizeStoreName(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function storeNamesMatch(a, b) {
  const na = normalizeStoreName(a);
  const nb = normalizeStoreName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const ta = na.split(" ");
  const tb = nb.split(" ");
  const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  return shorter.every((tok) => longer.includes(tok));
}

// Does this brand record claim the given store as a home
// (privateLabelFor: ["Costco"] etc.)?
function brandServesStore(rec, store) {
  const stores = Array.isArray(rec?.privateLabelFor) ? rec.privateLabelFor : null;
  if (!stores || stores.length === 0) return false;
  return stores.some((s) => storeNamesMatch(s, store));
}

// Local copy of the region match logic — duplicating here keeps
// brandExpertise.js dependency-free of the React hook layer (this
// module is imported by both browser and Node scripts).
function brandMatchesRegionLocal(meta, userRegion) {
  if (!meta) return true;
  if (meta.scope === "international") return true;
  const regions = Array.isArray(meta.regions) ? meta.regions : null;
  if (!regions || regions.length === 0) return true;
  const u = String(userRegion || "").toLowerCase();
  for (const r of regions) {
    if (typeof r !== "string") continue;
    const b = r.toLowerCase();
    if (u === b) return true;
    if (u.startsWith(b + "-")) return true;
    if (b.startsWith(u + "-")) return true;
  }
  return false;
}

// Internal slug → display resolver. Builds a one-time index from
// KNOWN_BRANDS so curated entries (where the slug is the only key)
// can render a properly capitalized display name with apostrophes
// and ampersands intact ("slim_jim" → "Slim Jim", "mms" → "M&M's",
// "boars_head" → "Boar's Head"). Falls back to a Title-Case rewrite
// of the slug for brands not in knownBrands.js — coverage is
// imperfect but better than rendering the raw slug.
let _slugToDisplay = null;
function slugToBrandDisplay(slug) {
  if (!_slugToDisplay) {
    const m = new Map();
    for (const entry of KNOWN_BRANDS) {
      const s = brandSlugify(entry.display);
      if (s && !m.has(s)) m.set(s, entry.display);
    }
    _slugToDisplay = m;
  }
  if (_slugToDisplay.has(slug)) return _slugToDisplay.get(slug);
  return String(slug)
    .split("_")
    .filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

// Sum the brand's total observation count across all canonicals AND
// USDA-derived category observations. Used as a tie-breaker when two
// candidates have equal canonical/hub scores ("how much do we know
// about this brand AT ALL?"). A brand with broad SKU breadth wins
// over a brand we barely know.
export function brandTotalObservations(brandDisplay) {
  const slug = brandSlugify(brandDisplay);
  if (!slug) return 0;
  let n = 0;
  const curated = BRAND_EXPERTISE[slug];
  if (curated) for (const v of Object.values(curated.canonicals || {})) n += Number(v) || 0;
  const derived = DERIVED[slug];
  if (derived) n += Number(derived.totalObs) || 0;
  return n;
}
